// app/admin/login.tsx
import { useMemo, useRef, useState } from 'react';
import { useRouter } from 'expo-router';
import {
  View,
  Text,
  TextInput,
  Pressable,
  ActivityIndicator,
  Alert,
  StyleSheet,
  Platform,
} from 'react-native';
import { Lock, Mail, Shield } from 'lucide-react-native';
import { supabase } from '@/utils/supabase';
import { COLORS, SPACING, RADIUS, TYPOGRAPHY, SHADOW } from '@/utils/adminUtils';

const MIN_PASSWORD_LEN = 12;

// Client-side throttle (NOT a real security control, but stops dumb brute forcing from the UI)
const MAX_ATTEMPTS = 3;
const LOCK_MS = 60_000;

export default function AdminLogin() {
  const router = useRouter();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);

  // naive in-memory limiter (resets on refresh)
  const attemptsRef = useRef(0);
  const lockUntilRef = useRef<number>(0);

  const now = Date.now();
  const locked = now < lockUntilRef.current;
  const lockSecondsLeft = locked ? Math.ceil((lockUntilRef.current - now) / 1000) : 0;

  const normalizedEmail = useMemo(() => email.trim().toLowerCase(), [email]);

  const disabled = useMemo(() => {
    if (loading) return true;
    if (locked) return true;
    if (!normalizedEmail) return true;
    if (password.length < MIN_PASSWORD_LEN) return true;
    return false;
  }, [loading, locked, normalizedEmail, password]);

  const bumpAttemptsAndMaybeLock = () => {
    attemptsRef.current += 1;
    if (attemptsRef.current >= MAX_ATTEMPTS) {
      lockUntilRef.current = Date.now() + LOCK_MS;
      attemptsRef.current = 0;
      Alert.alert('ננעל זמנית', `יותר מדי ניסיונות. נסה שוב בעוד ${Math.ceil(LOCK_MS / 1000)} שניות.`);
    }
  };

  const onLogin = async () => {
    if (disabled) {
      if (locked) {
        Alert.alert('ננעל זמנית', `נסה שוב בעוד ${lockSecondsLeft} שניות.`);
      } else if (password.length > 0 && password.length < MIN_PASSWORD_LEN) {
        Alert.alert('סיסמה חלשה', `סיסמה חייבת להיות לפחות ${MIN_PASSWORD_LEN} תווים.`);
      }
      return;
    }

    try {
      setLoading(true);

      const { data, error } = await supabase.auth.signInWithPassword({
        email: normalizedEmail,
        password,
      });

      if (error) {
        bumpAttemptsAndMaybeLock();
        Alert.alert('שגיאה', 'אימייל או סיסמה לא נכונים');
        return;
      }

      const userId = data.session?.user?.id;
      if (!userId) {
        bumpAttemptsAndMaybeLock();
        Alert.alert('שגיאה', 'לא התקבלה התחברות תקינה');
        return;
      }

      const { data: adminRow, error: adminErr } = await supabase
        .from('admins')
        .select('id, is_active, role')
        .eq('user_id', userId)
        .eq('is_active', true)
        .maybeSingle();

      if (adminErr || !adminRow) {
        await supabase.auth.signOut();
        Alert.alert('אין גישה', 'המשתמש הזה לא מוגדר כ-Admin');
        return;
      }

      await supabase
        .from('admins')
        .update({ last_login_at: new Date().toISOString() })
        .eq('id', adminRow.id);

      // Reset attempts on success
      attemptsRef.current = 0;
      lockUntilRef.current = 0;

      router.replace('/admin');
    } catch (e: any) {
      Alert.alert('שגיאה', e?.message ?? 'תקלה לא ידועה');
    } finally {
      setLoading(false);
    }
  };

  return (
    <View style={styles.page}>
      <View style={styles.glow} />

      <View style={styles.card}>
        <View style={styles.logoRow}>
          <View style={styles.logoIcon}>
            <Shield size={22} color={COLORS.primary} strokeWidth={2.5} />
          </View>
          <View>
            <Text style={styles.title}>Kursi Admin</Text>
            <Text style={styles.subtitle}>גישה למנהלים מורשים בלבד</Text>
          </View>
        </View>

        <View style={styles.divider} />

        {!!locked && (
          <Text style={styles.lockText}>ננעל זמנית. נסה שוב בעוד {lockSecondsLeft} שניות.</Text>
        )}

        <View style={styles.field}>
          <Text style={styles.label}>אימייל</Text>
          <View style={styles.inputWrapper}>
            <Mail size={15} color={COLORS.text.tertiary} strokeWidth={2} />
            <TextInput
              value={email}
              onChangeText={setEmail}
              autoCapitalize="none"
              keyboardType="email-address"
              placeholder="admin@kursi.com"
              placeholderTextColor={COLORS.text.tertiary}
              style={styles.input}
              editable={!loading && !locked}
            />
          </View>
        </View>

        <View style={styles.field}>
          <Text style={styles.label}>סיסמה</Text>
          <View style={styles.inputWrapper}>
            <Lock size={15} color={COLORS.text.tertiary} strokeWidth={2} />
            <TextInput
              value={password}
              onChangeText={setPassword}
              secureTextEntry
              placeholder="••••••••••••"
              placeholderTextColor={COLORS.text.tertiary}
              style={styles.input}
              editable={!loading && !locked}
              onSubmitEditing={onLogin}
            />
          </View>
          <Text style={styles.passwordHint}>מינימום {MIN_PASSWORD_LEN} תווים</Text>
        </View>

        <Pressable
          onPress={onLogin}
          disabled={disabled}
          style={({ pressed }) => [
            styles.btn,
            disabled && styles.btnDisabled,
            pressed && !disabled && styles.btnPressed,
          ]}
        >
          {loading ? <ActivityIndicator color="#FFF" /> : <Text style={styles.btnText}>התחבר</Text>}
        </Pressable>

        {Platform.OS === 'web' && (
          <Text style={styles.hint}>אין קישור ציבורי לדף הזה. אם הגעת לפה במקרה — חזור.</Text>
        )}
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  page: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: SPACING.xl,
    backgroundColor: COLORS.bg,
  },
  glow: {
    position: 'absolute',
    top: '20%',
    width: 400,
    height: 400,
    borderRadius: RADIUS.full,
    backgroundColor: 'rgba(200,83,30,0.18)',
    ...(Platform.OS === 'web' ? ({ filter: 'blur(100px)' } as any) : {}),
  },
  card: {
    width: '100%',
    maxWidth: 420,
    padding: SPACING.xxl,
    borderRadius: RADIUS.xxl,
    backgroundColor: COLORS.surface,
    borderWidth: 1,
    borderColor: COLORS.border,
    gap: SPACING.lg,
    ...SHADOW,
    ...(Platform.OS === 'web'
      ? ({ backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)' } as any)
      : {}),
  },
  logoRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: SPACING.md,
  },
  logoIcon: {
    width: 44,
    height: 44,
    borderRadius: RADIUS.lg,
    backgroundColor: COLORS.primaryLight,
    borderWidth: 1,
    borderColor: COLORS.primaryBorder,
    alignItems: 'center',
    justifyContent: 'center',
  },
  title: { ...TYPOGRAPHY.h3, color: COLORS.text.primary },
  subtitle: { ...TYPOGRAPHY.caption, color: COLORS.text.secondary, marginTop: 2 },
  divider: { height: 1, backgroundColor: COLORS.borderLight },
  lockText: {
    ...TYPOGRAPHY.small,
    color: COLORS.text.secondary,
    textAlign: 'center',
    lineHeight: 18,
  },
  field: { gap: SPACING.sm },
  label: {
    ...TYPOGRAPHY.caption,
    color: COLORS.text.secondary,
    fontWeight: '600',
    textAlign: 'right',
  },
  inputWrapper: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: SPACING.sm,
    paddingHorizontal: SPACING.lg,
    paddingVertical: SPACING.md,
    backgroundColor: COLORS.bgSecondary,
    borderRadius: RADIUS.lg,
    borderWidth: 1,
    borderColor: COLORS.border,
  },
  input: {
    flex: 1,
    ...TYPOGRAPHY.body,
    color: COLORS.text.primary,
    textAlign: 'right',
    ...(Platform.OS === 'web' ? ({ outlineStyle: 'none' } as any) : {}),
  },
  passwordHint: {
    ...TYPOGRAPHY.small,
    color: COLORS.text.tertiary,
    textAlign: 'right',
    marginTop: 2,
  },
  btn: {
    paddingVertical: SPACING.lg,
    borderRadius: RADIUS.lg,
    backgroundColor: COLORS.primary,
    alignItems: 'center',
    marginTop: SPACING.sm,
  },
  btnDisabled: { opacity: 0.45 },
  btnPressed: { opacity: 0.85 },
  btnText: { ...TYPOGRAPHY.bodyMed, color: '#FFF', fontWeight: '700' },
  hint: {
    ...TYPOGRAPHY.small,
    color: COLORS.text.tertiary,
    textAlign: 'center',
    lineHeight: 18,
  },
});